/**
 * <ChatThreadsPage /> — saved chat threads (M25.3).
 *
 * Lists the conversations the user pinned via <PinThreadButton />.
 * Each can be reopened in the chat panel, renamed, or deleted.
 * Opening fires the M25.4 window event — the panel loads the
 * thread as a new parallel conversation.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bookmark,
  Check,
  MessageSquare,
  Pencil,
  Share2,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui";
import { api } from "../lib/api";
import { requestOpenChatThread } from "../lib/chat-explain";

export function ChatThreadsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["chat-threads"],
    queryFn: () => api.listChatThreads(),
  });

  // Share is idempotent — re-sharing returns the same code — so the
  // button always shares + copies the link in one step.
  const share = useMutation({
    mutationFn: (id: string) => api.shareChatThread(id),
    onSuccess: async (thread) => {
      if (thread.share_code) {
        const link = `${window.location.origin}/ai/shared/${thread.share_code}`;
        try {
          await navigator.clipboard.writeText(link);
        } catch {
          /* clipboard blocked — the thread is still shared */
        }
        setCopiedId(thread.id);
        window.setTimeout(() => setCopiedId(null), 2000);
      }
      void queryClient.invalidateQueries({
        queryKey: ["chat-threads"],
      });
    },
  });

  const unshare = useMutation({
    mutationFn: (id: string) => api.unshareChatThread(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["chat-threads"] }),
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.renameChatThread(id, name),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["chat-threads"] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteChatThread(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["chat-threads"] }),
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("nav.chat_threads")}
        subtitle={t("policies.chat_threads_subtitle")}
        breadcrumbs={[{ label: t("nav.ai"), href: "/ai" }]}
      />

      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorPanel message={(q.error as Error).message} />
      ) : !q.data || q.data.length === 0 ? (
        <div className="rounded-xl border border-border-subtle bg-surface-default px-4 py-10 text-center">
          <Bookmark
            size={20}
            className="mx-auto mb-2 text-text-tertiary"
            aria-hidden
          />
          <p className="text-sm font-medium text-text-primary">
            {t("policies.chat_threads_no_saved")}
          </p>
          <p className="mt-1 text-xs text-text-secondary">
            {t("policies.chat_threads_no_saved_body")}
          </p>
        </div>
      ) : (
        <ul
          role="list"
          className="overflow-hidden rounded-xl border border-border-subtle bg-surface-default divide-y divide-border-subtle"
        >
          {q.data.map((thread) => (
            <li
              key={thread.id}
              className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-text-primary">
                  {thread.name}
                </p>
                <p className="text-xs text-text-tertiary">
                  {t(
                    thread.message_count === 1
                      ? "policies.chat_threads_message_count_one"
                      : "policies.chat_threads_message_count_many",
                    { count: thread.message_count },
                  )}
                  {" · "}
                  {thread.updated_at.slice(0, 10)}
                  {thread.is_shared ? t("policies.chat_threads_shared_suffix") : ""}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => requestOpenChatThread(thread.id)}
                  className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-2 py-1 text-xs font-medium text-text-secondary hover:bg-surface-sunken hover:text-text-primary"
                >
                  <MessageSquare size={12} /> {t("policies.chat_threads_open")}
                </button>
                <button
                  type="button"
                  onClick={() => share.mutate(thread.id)}
                  disabled={share.isPending}
                  aria-label={`Share ${thread.name}`}
                  title={t("policies.chat_threads_share_title")}
                  className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-2 py-1 text-xs font-medium text-text-secondary hover:bg-surface-sunken hover:text-text-primary disabled:opacity-50"
                >
                  {copiedId === thread.id ? (
                    <>
                      <Check size={12} /> {t("policies.chat_threads_copied")}
                    </>
                  ) : (
                    <>
                      <Share2 size={12} /> {t("policies.chat_threads_share")}
                    </>
                  )}
                </button>
                {thread.is_shared && (
                  <button
                    type="button"
                    onClick={() => unshare.mutate(thread.id)}
                    aria-label={`Stop sharing ${thread.name}`}
                    className="text-[11px] text-text-tertiary hover:text-rose-600"
                  >
                    {t("policies.chat_threads_unshare")}
                  </button>
                )}
                <button
                  type="button"
                  aria-label={`Rename ${thread.name}`}
                  onClick={() => {
                    const name = window
                      .prompt(t("policies.chat_threads_rename_prompt"), thread.name)
                      ?.trim();
                    if (name && name !== thread.name) {
                      rename.mutate({ id: thread.id, name });
                    }
                  }}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-tertiary hover:bg-surface-sunken hover:text-text-primary"
                >
                  <Pencil size={13} />
                </button>
                <button
                  type="button"
                  aria-label={`Delete ${thread.name}`}
                  onClick={() => {
                    if (
                      window.confirm(
                        t("policies.chat_threads_delete_confirm", { name: thread.name }),
                      )
                    ) {
                      remove.mutate(thread.id);
                    }
                  }}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-tertiary hover:bg-rose-50 hover:text-rose-600"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
